import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { Box, Image, Proportion } from '@wix/design-system';
import { TemplateGalleryCard } from '@common/types';
import { templatePath } from '@components/Hooks/useRouter';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { usePriceCalculation } from '@components/Hooks/usePriceCalculation';
import { fillImageUrl } from '@utils/urlUtils';
import CarouselStudioBranding from '@components/Templates/TemplateGallery/CarouselStudioBranding';
import CardInfo from '../CardInfo';
import s from '../TemplateCard.module.scss';

export interface TemplateCardMobileSimpleProps {
  template: TemplateGalleryCard;
  onCustomClick?: () => void;
  actionButton?: React.ReactNode;
}

const TemplateCardMobileSimple: FC<TemplateCardMobileSimpleProps> = ({
  template,
  onCustomClick,
  actionButton,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { logBiClick } = useTemplatesBi({ is_mobile: true });
  const { getLocalizedTotalPrice } = usePriceCalculation();

  const handleClick = useCallback(() => {
    if (onCustomClick) {
      onCustomClick();
    } else {
      logBiClick({
        click_type: 'Use template',
        click_data: { template_id: template.id },
      });
    }
  }, [onCustomClick, logBiClick, template.id]);

  const templateHref = useMemo(() => {
    if (!template.id) return undefined;
    return `${templatePath}/${template.id}`;
  }, [template.id]);

  const firstImage = template.media?.additionalImages?.[0];
  const imageUrl = firstImage?.url ?? '';
  const imageDescription = firstImage?.description ?? 'template image';

  const card = (
    <Box
      className={`${s.template} ${s.mobileView}`}
      direction="vertical"
      overflow="hidden"
      dataHook="mobile-simple-card"
    >
      <div style={{ overflow: 'hidden', position: 'relative', height: '100%' }}>
        <Box direction="vertical" className={s.cardCarousel}>
          <Box position="relative" overflow="hidden" padding="12px 12px 0 12px">
            <Proportion aspectRatio={8 / 5}>
              <Image
                height="100%"
                fit="cover"
                borderRadius="2px 2px 0 0"
                transparent
                alt={imageDescription}
                src={fillImageUrl(imageUrl, 972, 607)}
              />
            </Proportion>
          </Box>
        </Box>
        <CarouselStudioBranding isMobileView={true} />
      </div>
      <CardInfo
        template={template}
        isMobileView={true}
        titleProps={{ weight: 'normal', size: 'medium' }}
        priceTextProps={{
          weight: 'normal',
          getLocalizedPrice: getLocalizedTotalPrice,
        }}
        t={t}
        language={language}
      />
    </Box>
  );

  const WrapperTag = templateHref ? 'a' : 'div';
  const wrapperProps = templateHref
    ? {
        href: templateHref,
        onClick: handleClick,
        'aria-label': t('marketplace.templates-page.view-template-cta'),
      }
    : {
        'aria-label': t('marketplace.templates-page.view-template-cta'),
      };

  return (
    <div
      aria-labelledby={`template-title-${template.title}`}
      className={s.cardWrapper}
    >
      <WrapperTag
        data-hook={`template-card-mobile-simple-container-${template.id}`}
        className={s.cardContainer}
        {...wrapperProps}
      >
        {card}
      </WrapperTag>
      {actionButton && (
        <div
          className={s.templateCardActionButton}
          onClick={(e) => e.stopPropagation()}
        >
          {actionButton}
        </div>
      )}
    </div>
  );
};

export default TemplateCardMobileSimple;
